import { AUTH_STORAGE_KEY, CSRFTOKEN } from "@common/constants";
import { CSRF_HEADER, AUTH_HEADER } from "@common/constants/headers";
import {
  AxiosError,
  AxiosPromise,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";
import axios from "axios";
import { cleanAuthenticatedUser } from "@services/user/hooks/useAuthenticatedUser";
import { isApiError } from "@common/api";

const apiConfig: AxiosRequestConfig = {
  baseURL: __symphony_env__.REACT_APP_SERVER_URL,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json; charset=UTF-8",
  },
};

const instance = axios.create(apiConfig);

const responseInterceptor = (
  response: AxiosResponse<any, any>
): AxiosResponse<any, any> => {
  if (response.headers[CSRF_HEADER]) {
    localStorage.setItem(CSRFTOKEN, response.headers[CSRF_HEADER]);
  }
  return response;
};

const rejectInterceptor = (error: ApiError): AxiosPromise => {
  if (isApiError(error)) {
    const onTimeout = () => {
      cleanAuthenticatedUser();
      if (error.response.data.error) {
        error.response.data.error = "com.avispl.symphony.logout";
        return Promise.reject(error);
      } else {
        return Promise.reject({
          isAxiosError: true,
          response: {
            status: error.response.status,
            statusText: "",
            data: {
              error: "com.avispl.symphony.logout",
              message: "",
            },
          },
        });
      }
    };

    switch (error.response.data.error) {
      case "org.springframework.security.web.csrf.InvalidCsrfTokenException":
        return reloadTokenAndRetry(error.config as InternalAxiosRequestConfig);
      case "com.avispl.symphony.auth.common.error.SymphonyInvalidTokenException":
      case "org.springframework.security.web.csrf.MissingCsrfTokenException":
        return onTimeout();
      case "com.avispl.symphony.auth.error.SymphonyUserNotActiveException":
      case "com.avispl.symphony.auth.error.SymphonyAccessDeniedException":
      case "com.avispl.symphony.auth.common.error.SymphonyAccessDeniedException":
      case "com.avispl.symphony.auth.error.SymphonyAccountNotActiveException":
      case "com.avispl.symphony.auth.common.error.SymphonyLoginLockoutException":
      case "com.avispl.symphony.auth.error.SymphonyUserDeactivatedDueToInactivityException":
      case "com.avispl.symphony.auth.common.error.SymphonyAuthenticationException":
        cleanAuthenticatedUser();
        return Promise.reject(error);
      default:
        // Only thing we can rely on for SAML revoked session is the 401 status.
        if (error.response.status === 401 || error.response.status === 403)
          return onTimeout();
        return Promise.reject(error);
    }
  } else {
    return Promise.reject({
      isAxiosError: true,
      response: {
        status: 503,
        statusText: "Network Error",
        data: {
          error: "Network Error",
          message:
            "Something is temporarily wrong with your network connection. Please make sure you are connected to the internet and then reload your browser.",
        },
      },
    });
  }
};

const requestInterceptor = async (config: InternalAxiosRequestConfig<any>) => {
  const csrfToken = await getToken();
  const authToken = config.headers?.[AUTH_HEADER] ?? getAuthToken();
  return {
    ...config,
    headers: {
      ...config.headers,
      [CSRF_HEADER]: csrfToken,
      ...(authToken ? { [AUTH_HEADER]: authToken } : {}),
    },
  } as unknown as InternalAxiosRequestConfig<any>;
};

instance.interceptors.response.use(responseInterceptor, rejectInterceptor);
instance.interceptors.request.use(requestInterceptor);

export const reloadToken = async (): Promise<string> => {
  const optionsCall = await axios.get(
    `${__symphony_env__.REACT_APP_SERVER_URL}/symphony-api/public/version`,
    {
      ...apiConfig,
      withCredentials: true,
    }
  ); // lets call with static to avoid loop
  const csrfToken = optionsCall.headers[CSRF_HEADER];
  localStorage.setItem(CSRFTOKEN, csrfToken);
  return csrfToken;
};

export const getToken = async (): Promise<string> => {
  const token = localStorage.getItem(CSRFTOKEN);
  return token ?? (await reloadToken());
};

const getAuthToken = (): string => {
  return localStorage.getItem(AUTH_STORAGE_KEY) ?? "";
};

const retry = (config: AxiosRequestConfig): AxiosPromise => {
  return instance(config);
};

const reloadTokenAndRetry = (config: AxiosRequestConfig): AxiosPromise => {
  return reloadToken().then(() => retry(config));
};

export type ApiErrorResponse = AxiosResponse & {
  status: number;
  statusText: string;
  data: {
    details: string;
    error: string;
    message: string;
  };
};

export type ApiError = AxiosError & {
  response: ApiErrorResponse;
};
export type QueryType = "provisioning" | "monitoring";
export interface QueryTypeOption {
  queryType?: QueryType;
}
export interface ListOptions {
  maxResults?: number;
  orderBy?: string;
  startIndex?: number;
  totalLimit?: number;
}

export default instance;
